import Vue from 'vue'
import Router from 'vue-router'
import store from '@/store'
import i18n from '@/plugins/vue-i18n'
import generatedRoutes from "./generated-routes";
import auth from './auth'
import { checkVersion } from '../../versionChecker'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  routes: [
    ...auth,
    {
      path: '/',
      component: () => import('@/layouts/LayoutDefault.vue'),
      children: [
        {
          path: '/',
          beforeEnter: (to, from, next) => {
            const profile = store.getters['ProfileModule/profile']
            next(profile?.id ? { name: 'PageUsersEdit', params: { id: profile.id } } : { name: 'PageOffersList' })
          },
          component: { render: h => h('div') },
        },
        {
          path: '/log-viewer',
          name: 'PageLogViewer',
          meta: { title: 'Log Viewer', roles: ['ADMIN'], noRedirect: true },
          component: () => import('@/pages/log-viewer/index.vue'),
        },
        ...generatedRoutes,
        {
          path: '/403',
          name: 'PageError403',
          meta: { title: 'page.pageError403', noRedirect: true },
          component: () => import('@/pages/error/Error-403.vue'),
        },
        {
          path: '/404',
          name: 'PageError404',
          meta: { title: 'page.pageError404', noRedirect: true },
          component: () => import('@/pages/error/Error-404.vue'),
        },
      ],
    },

    // 404 page must be placed at the end !!!
    { path: '*', redirect: '/404', hidden: true },
  ],
})

const whiteList = ['loginPage', 'restorePage', 'recoveryPassword', 'createPassword', 'qrCodePage'] // no redirect whitelist
const forceWhiteList = ['recoveryPassword', 'createPassword'] // allowed even if already authenticated

router.beforeEach((to, from, next) => {
  // Ensure we checked auth before each page load.
  store.dispatch('AuthModule/onVerifyAuth').then(() => {
    const isAuthenticated = store.getters['AuthModule/isAuthenticated']
    if (isAuthenticated && whiteList.includes(to.name) && !forceWhiteList.includes(to.name)) {
      const profile = store.getters['ProfileModule/profile']
      next(profile?.id ? { name: 'PageUsersEdit', params: { id: profile.id } } : '/offers')
      return
    }

    if (whiteList.includes(to.name)) {
      next()
      return
    }

    if (!isAuthenticated) {
      next({ name: 'loginPage', query: to.meta?.noRedirect ? {} : { path: to.path, ...to.query } })
      return
    }

    // redirect on 403 when no permission
    const profilePermissions = store.getters['ProfileModule/profile'].permissions
    const profileRole = store.getters['ProfileModule/profile'].role
    const hasRolesDefined = Array.isArray(to.meta.roles) && to.meta.roles.length > 0
    const hasPermissionsDefined = Array.isArray(to.meta.permissions) && to.meta.permissions.length > 0

    const hasAccessByPermission = hasPermissionsDefined &&
      to.meta.permissions.some(permission => profilePermissions.includes(permission))
    const hasAccessByRole = hasRolesDefined &&
      to.meta.roles.includes(profileRole)

    let hasAccessPage = hasAccessByPermission || hasAccessByRole

    // If nothing is defined, allow access
    if (!hasRolesDefined && !hasPermissionsDefined) {
      hasAccessPage = true
    }
    // Ensure users can access their own profile edit page even without the specific role
    if (to.name === 'PageUsersEdit' && String(to.params.id) === String(store.getters['ProfileModule/profile'].id)) {
      hasAccessPage = true
    }

    if (!hasAccessPage) {
      next({ name: 'PageError403' })
      return
    }

    next()
  }).catch(() => {
    next({ name: 'loginPage' })
  })

  // close modals on change route
  if (to.name !== from.name) store.dispatch('ModalModule/closeAll')
  checkVersion()
  // set the page tab name
  if (to.meta?.title) document.title = i18n.t(to.meta.title)
})

router.onError(error => {
  const chunkLoadFailed = /loading chunk \d* failed./i.test(error.message)
  const unexpectedToken = /unexpected token/i.test(error.message)
  if (chunkLoadFailed || unexpectedToken) {
    // window.location.reload() reuses cache and loads stale index.html again.
    // Appending a query param forces the browser to request a fresh index.html from the server.
    const url = new URL(window.location.href)
    url.searchParams.set('_reload', Date.now())
    window.location.replace(url.toString())
  }
})

export default router
